import { useEffect } from 'react';
import { getAdSenseConfig } from './adsense-config';

/**
 * `<AdSenseScript>` — injects the AdSense page-level loader script
 * into the document `<head>` once per page when AdSense is enabled.
 * Idempotent — re-mounts (e.g., during navigation in a SPA) detect
 * an existing `<script>` and skip re-insertion.
 *
 * Per AdSense's getting-started guidance the loader script must be
 * present on every page that serves ads, but only one copy per
 * document load. Mounting this component once at the App root (per
 * `App.tsx`) satisfies both requirements without coupling to a
 * specific route.
 *
 * When AdSense is unconfigured (no `VITE_ADSENSE_CLIENT` /
 * `config.json` `adsense.client`), this component is a no-op so
 * the SPA does not load Google's script unnecessarily — a privacy +
 * page-weight win during the placeholder phase.
 */
export function AdSenseScript(): null {
  useEffect(() => {
    const { enabled, client } = getAdSenseConfig();
    if (!enabled || !client) return;
    const id = 'adsbygoogle-loader';
    if (document.getElementById(id)) return;
    const script = document.createElement('script');
    script.id = id;
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${encodeURIComponent(client)}`;
    document.head.appendChild(script);
  }, []);
  return null;
}
